import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { ThemeSwitcher } from "../components/theme/ThemeSwitcher";
import { Alert } from "../components/ui/Alert";
import { useAuth } from "../components/auth/AuthProvider";
import { AuthBrandingHeader } from "../components/auth/AuthBrandingHeader";
import { toast } from "../lib/toast";
import { usePublicConfig } from "../hooks/usePublicConfig";
import { useBrandingSettings } from "../lib/settings/useBrandingSettings";

type ActivationState = null | "email" | "admin";

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { data: publicConfig, loading: publicConfigLoading } = usePublicConfig();
  const branding = useBrandingSettings();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activationPending, setActivationPending] = useState<ActivationState>(null);
  const registrationEnabled = publicConfig?.registrationEnabled ?? true;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!registrationEnabled) {
      const message = "User registration is disabled by the administrator.";
      setError(message);
      toast.error(message);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, email, password })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message = data?.error?.message ?? data?.message ?? "Unable to register.";
        setError(message);
        toast.error(message);
        return;
      }

      // Handle activation modes
      if (data.activation === "email") {
        setActivationPending("email");
        toast.success("Account created! Check your email to activate.");
        return;
      }

      if (data.activation === "admin") {
        setActivationPending("admin");
        toast.success("Account created! Awaiting admin approval.");
        return;
      }

      // Mode 1: instant activation — session established
      toast.success("Account created. Welcome to QuickBox Pro.");
      await refresh();
      navigate("/profile", { replace: true });
    } catch {
      const message = "Network error. Please try again.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="surface-grid flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <AuthBrandingHeader
          title="Create your account"
          subtitle="Join the QuickBox Pro dashboard"
          authPages={publicConfig?.authPages}
          branding={branding.settings}
        />

        <Card className="glass-card">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Register</h2>
              <p className="text-sm text-muted-foreground">Basic details for your workspace.</p>
            </div>
            <ThemeSwitcher compact />
          </div>

          {!registrationEnabled && !publicConfigLoading ? (
            <div className="space-y-4">
              <Alert variant="warning">
                User registration is disabled by the administrator.
              </Alert>
              <Button type="button" size="lg" className="w-full" onClick={() => navigate("/login")}>
                Back to login
              </Button>
            </div>
          ) : activationPending ? (
            <div className="space-y-4">
              {activationPending === "email" ? (
                <Alert variant="info">
                  <div className="space-y-2">
                    <p className="font-medium">Check your email</p>
                    <p>We've sent a verification link to <strong>{email}</strong>. Click the link to activate your account.</p>
                    <p className="text-xs text-muted-foreground">The link expires in 24 hours. Check your spam folder if you don't see it.</p>
                  </div>
                </Alert>
              ) : (
                <Alert variant="info">
                  <div className="space-y-2">
                    <p className="font-medium">Awaiting approval</p>
                    <p>Your account has been created and is pending administrator approval. You'll be able to log in once approved.</p>
                  </div>
                </Alert>
              )}
              <Button type="button" size="lg" className="w-full" onClick={() => navigate("/login")}>
                Go to login
              </Button>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive-text">
                  {error}
                </div>
              )}

              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">Username</label>
                  <Input value={username} onChange={(event) => setUsername(event.target.value)} required />
                </div>
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">Email</label>
                  <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
                </div>
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">Password</label>
                  <Input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required />
                </div>
                <Button type="submit" size="lg" className="w-full" disabled={loading}>
                  {loading ? "Creating…" : "Create account"}
                </Button>
              </form>
              <p className="mt-4 text-center text-xs text-muted-foreground">
                Already have an account? <Link to="/login">Sign in</Link>
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};
